import { useCallback, useMemo, useState } from 'react';

/**
 * Currency detection + cycling, ported from the approved prototype's
 * `detect()`/`cycleCurrency` logic.
 *
 * **Prices are not here.** They live in `src/data/plans.ts` as bare numerals,
 * because local pricing is symbol-swap — the same number in every currency
 * (owner decision, 2026-07-30), which is what docs/landing/pricing.md §8.4 means
 * by "local prices, not FX conversions". This hook supplies the `symbol` that
 * gets prefixed, plus the tax label and the billing-entity note.
 *
 * What remains currency-varying is `big`: an illustrative *recovered value*
 * example, not a price. docs/landing/messaging.md §7 requires any such figure be
 * labelled an illustrative example with its formula available, and that labelling
 * is the caller's job — see `Duplicates.tsx`, which is the only caller.
 */

export type CurrencyCode = 'GBP' | 'EUR' | 'USD';

interface CurrencyInfo {
  code: CurrencyCode;
  name: string;
  symbol: string;
  region: string;
  /** Illustrative first-year recovery figure. Labelled by its caller. */
  big: string;
  raw: string;
  taxLabel: string;
  note: string;
}

const CCY: Record<CurrencyCode, CurrencyInfo> = {
  GBP: {
    code: 'GBP',
    name: 'pounds sterling',
    symbol: '£',
    region: 'UK',
    big: '5,280',
    raw: '5280',
    taxLabel: 'VAT',
    // "Change region" here means the BILLING region, not data residency — the two were
    // indistinguishable in the old wording, on a page about a residency product.
    note: 'UK workspaces are billed in GBP by Dedupr Ltd (London). Billing region is set at workspace creation, separately from data residency.',
  },
  EUR: {
    code: 'EUR',
    name: 'euro',
    symbol: '€',
    region: 'EU',
    big: '6,204',
    raw: '6204',
    taxLabel: 'VAT',
    note: 'EU workspaces are billed in EUR with reverse-charge VAT where a valid VAT ID is supplied.',
  },
  USD: {
    code: 'USD',
    name: 'US dollars',
    symbol: '$',
    region: 'US',
    big: '6,720',
    raw: '6720',
    taxLabel: 'sales tax',
    // ⚠️ Corrected 2026-07-30: this said "Residency can still be set to UK or EU".
    // `region` above is a BILLING region — which entity invoices you, in which
    // currency, under which tax label — and GBP billing from a London entity is
    // legitimate. **Data residency is a different axis and its only values are EU and
    // US** (`Region` enum in openapi.yaml). The UK is where the control plane lives
    // (ADR-057), holding our records about clients and never a client's records.
    note: 'Workspaces outside the UK and EU are billed in USD. Data residency is a separate choice — EU or US.',
  },
};

const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE', 'IS', 'NO', 'LI', 'CH',
]);

const ORDER: CurrencyCode[] = ['GBP', 'EUR', 'USD'];

function detect(): CurrencyCode {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    const loc = navigator.languages?.[0] ?? navigator.language ?? 'en-GB';
    const region = (loc.split('-')[1] ?? '').toUpperCase();
    if (tz === 'Europe/London' || region === 'GB' || region === 'UK') return 'GBP';
    if (EU_COUNTRY_CODES.has(region)) return 'EUR';
    if (tz.startsWith('Europe/')) return tz === 'Europe/London' ? 'GBP' : 'EUR';
    return 'USD';
  } catch {
    return 'GBP';
  }
}

export function useCurrency() {
  const [code, setCode] = useState<CurrencyCode>(() => detect());
  const cycle = useCallback(() => {
    setCode((current) => ORDER[(ORDER.indexOf(current) + 1) % ORDER.length]);
  }, []);
  const info = useMemo(() => CCY[code], [code]);
  return {
    ...info,
    regionLabel: info.region,
    moneyBig: info.symbol + info.big,
    cycle,
  };
}
